"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { Slider, Button, Card } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import "./GraphStatesVisualization.css";

function GraphStatesVisualization() {
  // Refs for all SVG elements
  const svgRef = useRef();
  const chainsSvgRef = useRef();
  const cyclesSvgRef = useRef();

  // All state declarations
  const [error, setError] = useState(null);
  const [currentStateIndex, setCurrentStateIndex] = useState(0);
  const [graphData, setGraphData] = useState(null);

  // Changed how we track displayed chains/cycles
  // Instead of accumulating them, we'll render only what should be visible at the current index
  const [chainsToDisplay, setChainsToDisplay] = useState([]);
  const [cyclesToDisplay, setCyclesToDisplay] = useState([]);

  const width = 800;
  const height = 800;
  const cycleSize = 150;

  const preprocessInfinity = (jsonString) => {
    return jsonString.replace(/Infinity/g, '"InfinityPlaceholder"');
  };

  const replaceInfinity = useCallback((obj) => {
    if (Array.isArray(obj)) {
      return obj.map(replaceInfinity);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, replaceInfinity(value)])
      );
    } else if (obj === "InfinityPlaceholder") {
      return Number.POSITIVE_INFINITY;
    }
    return obj;
  }, []);



  // New function to update which chains and cycles should be displayed
  const updateChainsAndCycles = useCallback(() => {
    if (!graphData) return;

    // Determine chains to display (all chains up to current index)
    const chains = [];
    const numChainsToShow = Math.min(
      currentStateIndex + 1,
      graphData.chains.length
    );

    for (let i = 0; i < numChainsToShow; i++) {
      const chain = graphData.chains[i];
      if (chain && Object.keys(chain).length > 0) {
        const sortedChain = {};
        const orderedNodes = sortChainNodes(chain);

        // Reconstruct chain with sorted nodes
        for (let j = 0; j < orderedNodes.length - 1; j++) {
          const currentNode = orderedNodes[j];
          const nextNode = orderedNodes[j + 1];

          if (chain[currentNode] && chain[currentNode][nextNode]) {
            sortedChain[currentNode] = {
              [nextNode]: chain[currentNode][nextNode],
            };
          }
        }

        chains.push(sortedChain);
      }
    }

    // Determine cycles to display (only if we're past the chains section)
    const cycles = [];
    if (currentStateIndex >= graphData.chains.length) {
      const cycleIndex = currentStateIndex - graphData.chains.length;

      for (let i = 0; i <= cycleIndex && i < graphData.cycles.length; i++) {
        cycles.push(graphData.cycles[i]);
      }
    }

    setChainsToDisplay(chains);
    setCyclesToDisplay(cycles);
  }, [currentStateIndex, graphData]);

  const createVisualization = useCallback((graphState) => {
    try {
      const nodes = new Set();
      const links = [];

      // Collect nodes and links
      Object.entries(graphState).forEach(([source, targets]) => {
        nodes.add(source);
        Object.entries(targets).forEach(([target, value]) => {
          nodes.add(target);
          links.push({
            source,
            target,
            units:
              value === Number.POSITIVE_INFINITY
                ? Number.POSITIVE_INFINITY
                : value.units,
            secondLegTotal:
              value === Number.POSITIVE_INFINITY
                ? Number.POSITIVE_INFINITY
                : value.second_leg_total,
          });
        });
      });

      const nodesArray = Array.from(nodes).map((id) => {
        let nodeType = "other";
        if (id.startsWith("MM")) nodeType = "MM";
        else if (id.startsWith("RM")) nodeType = "RM";
        else if (id.startsWith("BD")) nodeType = "BD";
        else if (id === "s" || id === "t") nodeType = "special";

        // Set initial x positions based on node type
        let x = width / 2;
        if (id === "s") x = 50;
        else if (id === "t") x = width - 50;
        else if (nodeType === "MM") x = width * 0.25;
        else if (nodeType === "BD") x = width * 0.5;
        else if (nodeType === "RM") x = width * 0.75;

        return {
          id,
          type: nodeType,
          x,
          fx: id === "s" ? 50 : id === "t" ? width - 50 : null,
        };
      });

      const simulation = d3
        .forceSimulation(nodesArray)
        .force(
          "link",
          d3
            .forceLink(links)
            .id((d) => d.id)
            .distance(150)
        )
        .force("charge", d3.forceManyBody().strength(-2000))
        .force("collision", d3.forceCollide().radius(50))
        // Custom force to maintain x-position constraints
        .force(
          "x",
          d3
            .forceX()
            .x((d) => {
              if (d.type === "MM") return width * 0.25;
              if (d.type === "BD") return width * 0.5;
              if (d.type === "RM") return width * 0.75;
              return d.x;
            })
            .strength(1)
        )
        // Vertical spacing
        .force("y", d3.forceY(height / 2).strength(0.1));

      const svg = d3
        .select(svgRef.current)
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [0, 0, width, height])
        .attr("style", "max-width: 100%; height: auto;");

      svg.selectAll("*").remove();

      // Arrow marker for non-infinity edges
      svg
        .append("defs")
        .append("marker")
        .attr("id", "arrowhead")
        .attr("viewBox", "-0 -5 10 10")
        .attr("refX", 23)
        .attr("refY", 0)
        .attr("orient", "auto")
        .attr("markerWidth", 4)
        .attr("markerHeight", 4)
        .append("svg:path")
        .attr("d", "M 0,-5 L 10 ,0 L 0,5")
        .attr("fill", "#666")
        .style("stroke", "none");

      // Create edges
      const link = svg
        .append("g")
        .selectAll("line")
        .data(links)
        .join("line")
        .attr("class", "link")
        .attr("marker-end", (d) =>
          d.units === Number.POSITIVE_INFINITY ? "" : "url(#arrowhead)"
        )
        .style("stroke", (d) =>
          d.units === Number.POSITIVE_INFINITY ? "#696868" : "#666"
        )
        .style("stroke-width", (d) =>
          d.units === Number.POSITIVE_INFINITY
            ? 1
            : Math.max(1, Math.sqrt(d.units) * 1.5)
        )
        .style("stroke-dasharray", (d) =>
          d.units === Number.POSITIVE_INFINITY ? "5,5" : "none"
        );

      // Edge labels (only for non-infinity edges)
      const linkLabels = svg
        .append("g")
        .selectAll("text")
        .data(links.filter((d) => d.units !== Number.POSITIVE_INFINITY))
        .join("text")
        .attr("class", "edge-label")
        .style("font-size", "12px")
        .style("text-anchor", "middle")
        .text((d) => `${d.units} T | $${d.secondLegTotal}`);

      const colorScale = d3
        .scaleOrdinal()
        .domain(["MM", "RM", "BD", "special", "other"])
        .range(["#69b3a2", "#ff7f50", "#6495ed", "#ffd700", "#808080"]);

      const node = svg
        .append("g")
        .selectAll("circle")
        .data(nodesArray)
        .join("circle")
        .attr("class", "node")
        .attr("r", 20)
        .style("fill", (d) => colorScale(d.type))
        .style("stroke", "#fff")
        .style("stroke-width", 2)
        .call(drag(simulation));

      const labels = svg
        .append("g")
        .selectAll("text")
        .data(nodesArray)
        .join("text")
        .text((d) => d.id)
        .attr("font-size", "14px")
        .attr("font-weight", "bold")
        .attr("text-anchor", "middle")
        .attr("dy", ".35em")
        .style("fill", "#fff");

      simulation.on("tick", () => {
        // Constrain x positions based on node type
        nodesArray.forEach((d) => {
          if (d.type === "MM") {
            d.x = Math.max(width * 0.2, Math.min(width * 0.3, d.x));
          } else if (d.type === "BD") {
            d.x = Math.max(width * 0.4, Math.min(width * 0.6, d.x));
          } else if (d.type === "RM") {
            d.x = Math.max(width * 0.7, Math.min(width * 0.8, d.x));
          }
          d.y = Math.max(20, Math.min(height - 20, d.y));
        });

        link
          .attr("x1", (d) => d.source.x)
          .attr("y1", (d) => d.source.y)
          .attr("x2", (d) => d.target.x)
          .attr("y2", (d) => d.target.y);

        linkLabels
          .attr("x", (d) => (d.source.x + d.target.x) / 2)
          .attr("y", (d) => (d.source.y + d.target.y) / 2 - 10);

        node.attr("cx", (d) => d.x).attr("cy", (d) => d.y);

        labels.attr("x", (d) => d.x).attr("y", (d) => d.y);
      });
    } catch (error) {
      console.error("Error creating visualization:", error);
      setError("Failed to create visualization: " + error.message);
    }
  }, []);
  useEffect(() => {
    const fetchGraphData = async () => {
      try {
        const response = await fetch("https://api.repo.tradingmechanism.com/get_final");
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch graph data");
        }

        const responseText = await response.text();
        const preprocessed = preprocessInfinity(responseText);
        let processedData;

        try {
          processedData = JSON.parse(preprocessed);
          processedData = replaceInfinity(processedData);
        } catch (error) {
          console.error("Error parsing processed data:", error);
          throw new Error("Failed to parse graph data");
        }

        setGraphData(processedData);
        if (processedData.graph_states && processedData.graph_states[0]) {
          createVisualization(processedData.graph_states[0]);
        }
      } catch (error) {
        console.error("Error fetching graph data:", error);
        setError(error.message);
      }
    };

    fetchGraphData();
  }, [createVisualization, replaceInfinity]);

  useEffect(() => {
    if (graphData?.graph_states?.[currentStateIndex]) {
      createVisualization(graphData.graph_states[currentStateIndex]);

      // Update chains and cycles to display based on current index
      updateChainsAndCycles();
    }
  }, [currentStateIndex, graphData, createVisualization, updateChainsAndCycles]);
  const drag = (simulation) => {
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      // Constrain dragging based on node type
      const node = event.subject;
      if (node.type === "MM") {
        event.subject.fx = Math.max(
          width * 0.2,
          Math.min(width * 0.3, event.x)
        );
      } else if (node.type === "BD") {
        event.subject.fx = Math.max(
          width * 0.4,
          Math.min(width * 0.6, event.x)
        );
      } else if (node.type === "RM") {
        event.subject.fx = Math.max(
          width * 0.7,
          Math.min(width * 0.8, event.x)
        );
      }
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      if (event.subject.id !== "s" && event.subject.id !== "t") {
        event.subject.fx = null;
        event.subject.fy = null;
      }
    }

    return d3
      .drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  };

  const sortChainNodes = (chainAdjList) => {
    if (!chainAdjList) return [];

    // Find the starting node (node with no incoming edges)
    const allNodes = new Set(Object.keys(chainAdjList));
    const targetNodes = new Set();
    Object.values(chainAdjList).forEach((targets) => {
      Object.keys(targets).forEach((target) => targetNodes.add(target));
    });

    const startNodes = Array.from(allNodes).filter(
      (node) => !targetNodes.has(node)
    );
    if (startNodes.length === 0) return Array.from(allNodes); // fallback if no clear start

    // Perform topological sort
    const ordered = [];
    const visited = new Set();

    const visit = (node) => {
      if (!visited.has(node)) {
        visited.add(node);
        if (chainAdjList[node]) {
          Object.keys(chainAdjList[node]).forEach((neighbor) => {
            visit(neighbor);
          });
        }
        ordered.unshift(node);
      }
    };

    startNodes.forEach((node) => visit(node));
    return ordered;
  };

  const renderChain = (chain, index) => {
    // Create a separate group for each chain with a FIXED vertical position
    const svg = d3.select(chainsSvgRef.current);
    const chainG = svg
      .append("g")
      .attr("transform", `translate(0, ${-350 + index * 100})`); // Fixed vertical spacing

    const nodes = [];
    const links = [];

    // Build nodes and links
    Object.entries(chain).forEach(([source, targets]) => {
      if (!nodes.includes(source)) nodes.push(source);
      Object.entries(targets).forEach(([target, value]) => {
        if (!nodes.includes(target)) nodes.push(target);
        links.push({ source, target, value });
      });
    });

    // Color coding function
    const getNodeColor = (node) => {
      if (node.startsWith("MM")) return "#69b3a2";
      if (node.startsWith("BD")) return "#6495ed";
      if (node.startsWith("RM")) return "#ff7f50";
      return "#808080";
    };

    // Calculate node spacing using percentages
    const nodeSpacing = 100 / (nodes.length + 1);
    const nodeRadius = 20;

    // Render links first (behind nodes)
    chainG
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("x1", (d) => `${(nodes.indexOf(d.source) + 1) * nodeSpacing}%`)
      .attr("y1", "50%")
      .attr("x2", (d) => `${(nodes.indexOf(d.target) + 1) * nodeSpacing}%`)
      .attr("y2", "50%")
      .attr("stroke", "#666")
      .attr("stroke-width", 2)
      .attr("marker-end", "url(#arrowhead)");

    // Render nodes (on top of links)
    chainG
      .selectAll("circle")
      .data(nodes)
      .join("circle")
      .attr("cx", (d, i) => `${(i + 1) * nodeSpacing}%`)
      .attr("cy", "50%")
      .attr("r", nodeRadius)
      .attr("fill", getNodeColor)
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);

    // Render node labels
    chainG
      .selectAll(".node-label")
      .data(nodes)
      .join("text")
      .attr("x", (d, i) => `${(i + 1) * nodeSpacing}%`)
      .attr("y", "50%")
      .attr("dy", ".35em")
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .text((d) => d);
  };
  const renderCycle = (cycle, index) => {
    const [cycleData, multiplier] = cycle;
    const svg = d3.select(cyclesSvgRef.current);
    const cycleG = svg
      .append("g")
      .attr(
        "transform",
        `translate(${(index % 2) * (cycleSize + 50)}, ${
          Math.floor(index / 2) * (cycleSize + 50)
        })`
      );

    // Extract nodes and links
    const nodes = new Set();
    const links = [];

    Object.entries(cycleData).forEach(([source, targets]) => {
      nodes.add(source);
      Object.entries(targets).forEach(([target, value]) => {
        nodes.add(target);
        links.push({ source, target, value });
      });
    });

    const nodesArray = Array.from(nodes).map((id, i) => {
      const angle = (i * 2 * Math.PI) / nodes.size;
      return {
        id,
        x: cycleSize / 2 + 40 * Math.cos(angle),
        y: cycleSize / 2 + 40 * Math.sin(angle),
      };
    });

    // Add links
    cycleG
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("x1", (d) => nodesArray.find((n) => n.id === d.source).x)
      .attr("y1", (d) => nodesArray.find((n) => n.id === d.source).y)
      .attr("x2", (d) => nodesArray.find((n) => n.id === d.target).x)
      .attr("y2", (d) => nodesArray.find((n) => n.id === d.target).y)
      .attr("stroke", "#6495ed")
      .attr("stroke-width", 2)
      .attr("marker-end", "url(#arrowhead)");

    // Add nodes
    cycleG
      .selectAll("circle")
      .data(nodesArray)
      .join("circle")
      .attr("cx", (d) => d.x)
      .attr("cy", (d) => d.y)
      .attr("r", 20)
      .attr("fill", "#6495ed")
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);

    // Add node labels
    cycleG
      .selectAll("text")
      .data(nodesArray)
      .join("text")
      .attr("x", (d) => d.x)
      .attr("y", (d) => d.y)
      .attr("dy", ".35em")
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .text((d) => d.id);

    // Add multiplier label if provided
    if (multiplier) {
      cycleG
        .append("text")
        .attr("x", cycleSize / 2)
        .attr("y", cycleSize + 20)
        .attr("text-anchor", "middle")
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .text(`× ${multiplier}`);
    }
  };

  useEffect(() => {
    // Clear and recreate chain visualizations
    const svg = d3.select(chainsSvgRef.current);
    svg.selectAll("*").remove(); // Clear previous chain renderings

    // Set a fixed height for the chains SVG that's large enough for all possible chains
    // This prevents the container from growing and shifting content
    const maxPossibleChains = graphData?.chains?.length || 10; // Use a reasonable default if data not loaded
    const fixedHeight = maxPossibleChains * 100;
    svg.attr("height", fixedHeight);

    chainsToDisplay.forEach((chain, index) => {
      renderChain(chain, index);
    });

    // Clear and recreate cycle visualizations
    if (cyclesSvgRef.current) {
      const cyclesSvg = d3.select(cyclesSvgRef.current);
      cyclesSvg.selectAll("*").remove();

      // Set a fixed height for cycles SVG too
      const maxPossibleCycleRows = Math.ceil(
        (graphData?.cycles?.length || 4) / 2
      );
      const fixedCycleHeight = Math.max(
        150,
        maxPossibleCycleRows * (cycleSize + 50)
      );
      cyclesSvg.attr("height", fixedCycleHeight);

      cyclesToDisplay.forEach((cycle, index) => renderCycle(cycle, index));
    }
  }, [
    chainsToDisplay,
    cyclesToDisplay,
    graphData?.chains?.length,
    graphData?.cycles?.length,
  ]);

  return (
    <div className="visualization-container">
      <div className="graph-section">
        <div className="graph-container">
          {error && (
            <div className="text-red-700 bg-red-100 p-3 rounded">{error}</div>
          )}
          <svg ref={svgRef}></svg>
        </div>

        <div className="controls-container">
          <div className="slider-container">
            <Button
              type="primary"
              icon={<LeftOutlined />}
              onClick={() =>
                setCurrentStateIndex((prev) => Math.max(0, prev - 1))
              }
              disabled={currentStateIndex === 0}
            >
              Previous
            </Button>

            <div className="slider-wrapper">
              <Slider
                value={currentStateIndex}
                onChange={(value) => setCurrentStateIndex(value)}
                min={0}
                max={(graphData?.graph_states?.length || 1) - 1}
                step={1}
              />
            </div>

            <Button
              type="primary"
              icon={<RightOutlined />}
              onClick={() =>
                setCurrentStateIndex((prev) =>
                  Math.min((graphData?.graph_states?.length || 1) - 1, prev + 1)
                )
              }
              disabled={
                currentStateIndex === (graphData?.graph_states?.length || 1) - 1
              }
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <div className="accumulation-section">
        <Card
          title="Chains"
          className="accumulation-card"
          bodyStyle={{ padding: 0, height: "100%" }}
        >
          <div className="accumulation-content">
            <div className="svg-container">
              <svg ref={chainsSvgRef} width="100%" height="100%" />
            </div>
          </div>
        </Card>

        <Card
          title="Cycles"
          className="accumulation-card"
          bodyStyle={{ padding: 0 }}
        >
          <div className="accumulation-content">
            <div className="svg-container">
              <svg ref={cyclesSvgRef} width="100%" height="100%" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default GraphStatesVisualization;
